import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { last, map, switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface SignedUrlResponse {
  signedUrl: string;
  fileNameWithPrefix: string;
  url: string;
  contentType: string;
}

export interface SignedUrlApiResponse {
  data: SignedUrlResponse;
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  constructor(private api: ApiService, private http: HttpClient) {}

  // Step 1: Get signed URL from backend
  private getSignedUrl(fileName: string, fileType: string): Observable<SignedUrlResponse> {
    return this.api.request({
      path: 'uploads/signed-url',
      method: 'POST',
      body: { fileName, contentType: fileType }
    }).pipe(
      map((res: SignedUrlApiResponse) => res.data)
    );
  }

  // Step 2: Upload file to S3 and track progress
  private uploadToS3(signedData: SignedUrlResponse, file: File, progress$: Subject<number>): Observable<void> {
    const req = new HttpRequest('PUT', signedData.signedUrl, file, {
      reportProgress: true,
      headers: new HttpHeaders({
        'Content-Type': signedData.contentType,
        'skip-auth': 'true'
      })
    });

    return this.http.request(req).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          const percentDone = Math.round((event.loaded / event.total) * 100);
          progress$.next(percentDone);
        }
        return event;
      }),
      last(),
      map(() => {
        progress$.next(100);
        return;
      })
    );
  }

  // Step 3: Notify server that asset was uploaded
  private notifyServer(data: SignedUrlResponse, file: File): Observable<any> {
    const assetData = {
      filePath: data.fileNameWithPrefix,
      mimeType: data.contentType,
      url: data.url,
      fileName: file.name,
    };

    return this.api.request({
      path: 'uploads/create',
      method: 'POST',
      body: assetData
    });
  }

  // Main upload controller
  startUpload(file: File): { progress$: Observable<number>, done$: Observable<any> } {
    const progress$ = new Subject<number>();
    const done$ = new Subject<any>();

    this.getSignedUrl(file.name, file.type)
      .pipe(
        switchMap(signedData => this.uploadToS3(signedData, file, progress$).pipe(
          switchMap(() => this.notifyServer(signedData, file))
        ))
      )
      .subscribe({
        next: result => {
          done$.next(result);
          done$.complete();
          progress$.complete();
        },
        error: err => {
          done$.error(err);
          progress$.error(err);
        }
      });

    return {
      progress$: progress$.asObservable(),
      done$: done$.asObservable()
    };
  }
}
